import React from "react";
import { snakeCaseToSentenceCase } from "utils/formatText";
import { OPERATOR_OPTIONS } from "./constants";
import { formatCurrency, formatPercent } from "utils/formatNumber";
import { listFormatter } from "utils/listFormatter";

const pluralBlacklist = ["acos", "roas", "tacos", "troas"];

const metricOptionLabels = {
  target_match_types: "targets",
  keyword_match_types: "keywords",
  conversions: "conversions",
  acos: "ACOS",
  roas: "ROAS",
  ad_spend: "ad spend",
  conv_rate: "conversion rate",
  ctr: "click-through rate",
  clicks: "clicks",
  bid: "current bid",
  impressions: "impressions",
  cpc: "cost-per-click",
  serving_hours: "serving hours",
  "ad.serving_hours": "serving hours",
  units: "units",
  bsr: "average BSR",
  tacos: "total ACOS",
  troas: "total ROAS",
  click_thru_rate: "click-through-rate",
  cost: "cost",
  ecpc: "eCPC",
  total_detailed_page_view_rate: "total DPV rate",
  total_add_to_cart_rate: "total ATC rate",
  new_to_brand_detail_page_view_rate: "NTB DPV rate",
  total_new_to_brand_dpv_rate: "total NTB DPV rate",
  brand_search_rate: "brand search rate",
  budget_percent: "spend % of budget",
  "ad.budget_percent": "spend % of budget",
  impression_trend: "impression trend %",
  clicks_trend: "clicks trend %",
  conversions_trend: "conversions trend %",
  ad_sales: "ad sales",
  ad_sales_trend: "ad sales trend %",
};

const actionOptionDescriptions = {
  USE_ALGO: "set optimal bid",
  DO_NOTHING: "keep current bid",
  PAUSE: "pause keyword or target",
  SET_BID_CPC: "set bid based on average CPC",
};

const sanitizeMetricOption = (value) => {
  const sanitizedValue = value.replace(/(^[^a-zA-Z0-9]+)|([^a-zA-Z0-9]+$)/g, "");

  return sanitizedValue;
};

const metricLabel = (metric) => metricOptionLabels[metric] ?? snakeCaseToSentenceCase(metric);

const formatConditionValue = (value) => {
  const formattedValue = value.replaceAll("_", " ");
  let returnString = "";

  for (const word of formattedValue.split(" ")) {
    if (metricOptionLabels[word]) {
      returnString += ` ${metricOptionLabels[word] ?? snakeCaseToSentenceCase(word)}`;
    } else {
      returnString += ` ${word}`;
    }
  }

  return returnString;
};

export const summarizeProgram = (scope, conditions, logicalOperator, actions, marketPlace) => {
  let summary = "When ";

  const matchTypeConditions = [];

  try {
    for (let i = 0; i < conditions.length; i++) {
      const condition = conditions[i];
      const { option, values, scope: conditionScope = null } = condition;
      let [operator, metric] = JSON.parse(option);
      let value = condition.values ?? [];

      if (typeof value?.[0] === "string") {
        value = sanitizeMetricOption(value?.[0]);
      }
      metric = sanitizeMetricOption(metric);

      if (["keyword_match_types", "target_match_types"].includes(metric)) {
        matchTypeConditions.push(condition);
        continue;
      }

      let conditionScopeText = "";
      if (conditionScope) {
        const days = conditionScope?.values?.days;
        if (days) {
          const dayText = parseInt(days) > 1 ? "days" : "day";
          conditionScopeText = `over ${days} ${dayText} `;
        }
      }

      let linkingVerb = "is";
      if (metric[metric.length - 1] === "s" && !pluralBlacklist.includes(metric)) {
        linkingVerb = "are";
      }

      operator = OPERATOR_OPTIONS["number"]?.find((o) => o.value === operator)?.label;

      const logicalOperatorDescription = logicalOperator === "any" ? "or" : "and";

      if (operator === "between") {
        summary += `${metricLabel(metric)} ${conditionScopeText}${linkingVerb} ${operator} ${values?.[0] ?? ""} and ${values?.[1] ?? ""}${
          i === conditions.length - 1 ? ""
          : i === conditions.length - 2 ? ` ${logicalOperatorDescription} `
          : ", "
        } `;
      } else {
        summary += `${metricLabel(metric)} ${conditionScopeText}${linkingVerb} ${operator} ${typeof value === "string" ? formatConditionValue(value) : value}${
          i === conditions.length - 2 ? ` ${logicalOperatorDescription} ` : ", "
        } `;
      }
    }

    for (let i = 0; i < matchTypeConditions.length; i++) {
      const condition = matchTypeConditions[i];
      const { option } = condition;
      let [, metric] = JSON.parse(option);
      const matchTypes = Array.isArray(condition.values) ? condition.values?.flat() : [];

      metric = sanitizeMetricOption(metric);

      if (matchTypes?.filter((m) => !!m).length === 0) {
        continue;
      }

      if (i === 0) {
        summary = summary.replace("then", "");
      }

      // Remove "and" before describing match type conditions
      summary = summary.slice(0, summary.lastIndexOf("and"));

      if (!matchTypes?.length || !Array.isArray(matchTypes) || matchTypes?.some((el) => typeof el !== "string")) {
        continue;
      }

      summary += ` for ${metricOptionLabels[metric]} with ${listFormatter.format(
        matchTypes,
      )} match types${i < matchTypeConditions.length - 1 ? " or " : " "}`;
    }

    for (let i = 0; i < actions.length; i++) {
      const action = actions[i];
      let { function: actionToPerform, values, operator } = action;

      let changeType = "by";

      if (actionToPerform.toLowerCase().includes("set")) {
        changeType = "to";
      }

      if (["USE_ALGO", "DO_NOTHING", "PAUSE"].includes(actionToPerform)) {
        changeType = "";
      }

      if (actionToPerform === "SET_BID_CPC") {
        let value;
        let sign = "";
        try {
          value = parseFloat(values[0]);
          sign = value > 0 ? " plus " : " minus ";
          if (value === 0) {
            sign = "";
            value = "";
          } else {
            value = Math.abs(value);
            value = operator === "adjust_fixed" ? formatCurrency(value, marketPlace) : formatPercent(value / 100);
          }
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
          value = "";
        }
        summary += `to the average CPC over the last ${values[1]} days${sign}${value}`;
      } else {
        if (actionOptionDescriptions[actionToPerform]) {
          actionToPerform = actionOptionDescriptions[actionToPerform];
        }

        summary += `${snakeCaseToSentenceCase(actionToPerform).toLowerCase()}${changeType ? ` ${changeType} ` : ""}${
          !changeType ? ""
          : operator === "adjust_fixed" ? formatCurrency(values[0], marketPlace)
          : formatPercent(values[0] / 100)
        }
      `;
      }
      summary +=
        i === actions.length - 1 ? ""
        : i === actions.length - 2 ? " and "
        : ", ";
    }
    summary += ".";

    return summary;
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e);
    return "Error generating summary";
  }
};

const METRICS = [
  "targets",
  "keywords",
  "conversions",
  "max",
  "min",
  "target",
  "acos",
  "total",
  "roas",
  "ad",
  "sales",
  "trend",
  "%",
  "budget",
  "serving",
  "hours",
  "spend",
  "conversion",
  "rate",
  "click-through-rate",
  "clicks",
  "current",
  "keep",
  "set",
  "optimal",
  "bid",
  "impressions",
  "auto",
  "product",
  "category",
  "dynamic",
  "expression",
  "broad",
  "exact",
  "phrase",
  "keyword",
  "target",
  "cost-per-click",
  "cost",
  "ecpc",
  "atc",
  "ntb",
  "dpv",
  "brand",
  "search",
];

const OPERATORS = ["greater", "less", "than", "or", "equal", "to", "between"];
const ACTIONS = ["increase", "decrease", "set", "pause"];

export const boldKeywords = (str = "", wordsToEmbolden = null) => {
  // If empty list or not formatted as an array, default to metrics and operators
  if (!wordsToEmbolden || typeof wordsToEmbolden !== "object") {
    wordsToEmbolden = [...METRICS, ...OPERATORS, ...ACTIONS];
  }

  // Embolden parameters in summary sentence
  const formatted = str?.split(" ")?.map((word, i) => {
    if (
      wordsToEmbolden?.includes(word.toLowerCase()) ||
      wordsToEmbolden?.includes(word.slice(0, -1).toLowerCase()) ||
      !isNaN(parseInt(word)) ||
      !isNaN(parseInt(word.slice(1)))
    ) {
      return (
        <strong key={word}>
          {word}
          {i === str?.split(" ")?.length - 1 ? "" : " "}
        </strong>
      );
    } else {
      return word + " ";
    }
  });

  return formatted;
};
